import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import { Calendar, MessageCircle, ThumbsUp, Filter, X, BarChart2 } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import TagSelector from '../../components/Tags/TagSelector';
import searchService from '../../services/searchService';
import './SearchResults.css';

function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: 'all', // all, today, week, month
    sortBy: 'relevance', // relevance, date, upvotes, comments
    tags: [],
    onlyWithAttachments: false,
  });
  
  // Read search query from URL query parameters
  const searchQuery = new URLSearchParams(location.search).get('q') || '';

  // Perform search with filters
  useEffect(() => {
    if (!searchQuery.trim()) {
      navigate('/browse');
      return;
    }
    
    setLoading(true);
    const fetchResults = async () => {
      try {
        // Start with a basic query
        const queriesRef = collection(db, "queries");
        let baseQuery = query(queriesRef);
        
        // Build the query based on filters
        if (filters.dateRange !== 'all') {
          const dateLimit = new Date();
          
          if (filters.dateRange === 'today') {
            dateLimit.setDate(dateLimit.getDate() - 1);
          } else if (filters.dateRange === 'week') {
            dateLimit.setDate(dateLimit.getDate() - 7);
          } else if (filters.dateRange === 'month') {
            dateLimit.setMonth(dateLimit.getMonth() - 1);
          }
          
          baseQuery = query(baseQuery, where('createdAt', '>=', dateLimit));
        }
        
        // Execute the query
        const qSnap = await getDocs(baseQuery);
        
        // Process the results with client-side filtering and sorting
        const results = qSnap.docs
          .map((doc) => ({ 
            id: doc.id, 
            ...doc.data(),
            // Calculate relevance score
            relevanceScore: calculateRelevanceScore(doc.data(), searchQuery)
          }))
          .filter(doc => {
            // Apply text search
            const matchesSearch = 
              doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              doc.description.toLowerCase().includes(searchQuery.toLowerCase());
            
            if (!matchesSearch) return false;
            
            // Filter by tags if any are selected
            if (filters.tags.length > 0) {
              const docTags = doc.tags || [];
              const hasMatchingTag = filters.tags.some(tag => docTags.includes(tag));
              if (!hasMatchingTag) return false;
            }
            
            // Filter for attachments if enabled
            if (filters.onlyWithAttachments && (!doc.attachments || doc.attachments.length === 0)) {
              return false;
            }
            
            return true;
          });
        
        // Sort the results
        const sortedResults = sortResults(results, filters.sortBy);
        
        // Add highlighted text
        const resultsWithHighlights = sortedResults.map(result => ({
          ...result,
          highlightedTitle: highlightText(result.title, searchQuery),
          highlightedDescription: highlightText(result.description, searchQuery, 150)
        }));
        
        // Track the search query
        const extractedTags = sortedResults.flatMap(result => result.tags || []);
        const uniqueTags = [...new Set(extractedTags)].slice(0, 5); // Take up to 5 unique tags
        await searchService.trackSearchQuery(searchQuery, uniqueTags, resultsWithHighlights);
        
        setSearchResults(resultsWithHighlights);
        setLoading(false);
      } catch (error) {
        console.error("Error searching:", error);
        setLoading(false);
      }
    };

    fetchResults();
  }, [searchQuery, navigate, filters]);

  // Calculate relevance score based on query matches
  const calculateRelevanceScore = (doc, query) => {
    const queryTerms = query.toLowerCase().split(/\s+/).filter(term => term.length > 0);
    let score = 0;
    
    // Title matches (weighted higher)
    queryTerms.forEach(term => {
      const titleLower = doc.title.toLowerCase();
      if (titleLower.includes(term)) {
        // Exact title match gets higher score
        score += titleLower === term ? 10 : 5;
        
        // Beginning of title match gets bonus
        if (titleLower.startsWith(term)) score += 3;
      }
    });
    
    // Description matches
    queryTerms.forEach(term => {
      const descLower = doc.description.toLowerCase();
      if (descLower.includes(term)) {
        score += 2;
      }
    });
    
    // Boost for recent documents
    if (doc.createdAt) {
      const now = new Date();
      const createdDate = doc.createdAt.toDate ? doc.createdAt.toDate() : new Date(doc.createdAt);
      const daysDiff = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff < 7) {
        score += (7 - daysDiff) * 0.5; // Up to 3.5 bonus points for very recent docs
      }
    }
    
    // Boost for engagement
    score += (doc.upvoteCount || 0) * 0.2;
    score += (doc.commentCount || 0) * 0.3;
    
    return score;
  };
  
  // Sort results based on selected sorting option
  const sortResults = (results, sortOption) => {
    switch (sortOption) {
      case 'relevance':
        return [...results].sort((a, b) => b.relevanceScore - a.relevanceScore);
      case 'date':
        return [...results].sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return dateB - dateA;
        });
      case 'upvotes':
        return [...results].sort((a, b) => (b.upvoteCount || 0) - (a.upvoteCount || 0));
      case 'comments':
        return [...results].sort((a, b) => (b.commentCount || 0) - (a.commentCount || 0));
      default:
        return results;
    }
  };
  
  // Highlight search terms in text
  const highlightText = (text, query, maxLength = null) => {
    if (!text) return '';
    
    const queryTerms = query.toLowerCase().split(/\s+/).filter(term => term.length > 0);
    let highlightedText = text;
    
    // If maxLength is provided and text is longer, truncate around the first match
    if (maxLength && text.length > maxLength) {
      let startIndex = 0;
      
      // Try to find the first match to center the truncation
      for (const term of queryTerms) {
        const index = text.toLowerCase().indexOf(term);
        if (index !== -1) {
          startIndex = Math.max(0, index - Math.floor(maxLength / 2));
          break;
        }
      }
      
      // Truncate the text
      highlightedText = (startIndex > 0 ? '...' : '') + 
                          text.substr(startIndex, maxLength) + 
                          (startIndex + maxLength < text.length ? '...' : '');
    }
    
    // Wrap matches in highlight spans
    queryTerms.forEach(term => {
      if (!term) return;
      
      const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<span class="search-highlight">$1</span>');
    });
    
    return highlightedText;
  };

  // Update filters and refresh search
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Replace the existing tags filter with TagSelector
  const toggleTagFilter = (tag) => {
    setFilters(prev => {
      const isSelected = prev.tags.includes(tag);
      return {
        ...prev,
        tags: isSelected
          ? prev.tags.filter(t => t !== tag)
          : [...prev.tags, tag]
      };
    });
  };
  
  // Reset all filters to default
  const resetFilters = () => {
    setFilters({
      dateRange: 'all',
      sortBy: 'relevance',
      tags: [],
      onlyWithAttachments: false
    });
  };

  const renderQueryCard = (query, showScore = false) => (
    <div 
      key={query.id} 
      className="query-card"
      onClick={() => navigate(`/query/${query.id}`)}
      style={{ cursor: 'pointer' }}
    >
      <div className="query-card-content">
        <h3 className="query-title" dangerouslySetInnerHTML={{ __html: query.highlightedTitle }}></h3>
        <p className="query-description" dangerouslySetInnerHTML={{ __html: query.highlightedDescription }}></p>
        
        {/* Display tags if available */}
        {query.tags && query.tags.length > 0 && (
          <div className="query-tags">
            {query.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="query-tag">{tag}</span>
            ))}
            {query.tags.length > 3 && <span className="query-tag-more">+{query.tags.length - 3}</span>}
          </div>
        )}
        
        {query.attachments?.length > 0 && (
          <div className="query-attachments">
            {query.attachments.slice(0, 1).map((url, idx) => (
              <img key={idx} src={url || "/placeholder.svg"} alt="attachment" className="query-attachment" />
            ))}
            {query.attachments.length > 1 && (
              <div className="attachment-count">+{query.attachments.length - 1}</div>
            )}
          </div>
        )}
        <div className="query-meta">
          {showScore ? (
            <div className="query-score">
              <BarChart2 size={16} />
              <span>Score: {query.relevanceScore.toFixed(1)}</span>
            </div>
          ) : (
            <>
              <div className="query-score">
                <ThumbsUp size={16} />
                <span>{query.upvoteCount || 0}</span>
              </div>
              <div className="query-comments">
                <MessageCircle size={16} />
                <span>{query.commentCount || 0}</span>
              </div>
            </>
          )}
          {query.createdAt && (
            <div className="query-date">
              {new Date(query.createdAt.toDate ? query.createdAt.toDate() : query.createdAt).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="search-results-container">
      <Navbar />
      <div className="search-results-content">
        <div className="search-results-header">
          <h1 className="page-title">Search Results for: <span className="search-query">{searchQuery}</span></h1>
          <div className="search-header-actions">
            <p className="results-count">{searchResults.length} results found</p>
            <Link to="/search-analytics" className="view-analytics-btn">
              <BarChart2 size={16} />
              View Search Analytics
            </Link>
          </div>
        </div>
        
        {/* Filters Section */}
        <div className="search-filters">
          <div className="filter-header">
            <Filter size={18} />
            <h3>Filters</h3>
            {(filters.dateRange !== 'all' || filters.sortBy !== 'relevance' || 
              filters.tags.length > 0 || filters.onlyWithAttachments) && (
              <button onClick={resetFilters} className="reset-filters-btn">
                <X size={14} />
                Reset
              </button>
            )}
          </div>
          
          <div className="filter-options">
            <div className="filter-group">
              <label>Date Range</label>
              <select 
                value={filters.dateRange} 
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="filter-select"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>Sort By</label>
              <select 
                value={filters.sortBy} 
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="filter-select"
              >
                <option value="relevance">Most Relevant</option>
                <option value="date">Most Recent</option>
                <option value="upvotes">Most Upvoted</option>
                <option value="comments">Most Commented</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>
                <input 
                  type="checkbox" 
                  checked={filters.onlyWithAttachments} 
                  onChange={(e) => handleFilterChange('onlyWithAttachments', e.target.checked)}
                />
                Only with Attachments
              </label>
            </div>
          </div>
          
          {/* Replace the tags filter section with TagSelector */}
          <div className="tags-filter">
            <label>Filter by Tags</label>
            <TagSelector 
              selectedTags={filters.tags}
              onTagToggle={toggleTagFilter}
              showPopular={true}
            />
          </div>
        </div>
        
        <section className="search-results-section query-section">
          <div className="section-header">
            <Calendar className="section-icon" size={20} />
            <h2>Results</h2>
          </div>
          
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Searching...</p>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="empty-state">
              <p>No results found.</p>
              <p className="suggestions">Try adjusting your search terms or filters.</p>
            </div>
          ) : (
            <div className="query-grid">
              {searchResults.map(query => renderQueryCard(query, filters.sortBy === 'relevance'))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default SearchResults;